/**
 * ILoader Interface Implementation for LoaderPreProcessJson
 * Contains methods from the ILoader base interface
 * Non-interface methods are in loaderPreProcessJson_Accessors.ts
 */

import { CommonUtil, parseComponentReusePattern } from '../../../../Assembler/dist/common/commonUtil.js';
import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { DynamicConfigMap, serializeDynamicConfigToJson as serializeDynamicConfigToJsonCore } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { InheritanceOperation, NavigationDecision } from '../../../../Assembler/dist/model/index.js';
import type { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import type { LoaderPreProcessJson } from './loaderPreProcessJson.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import {
    buildMetadataJsonForAppSite,
    getAllTemplatesJson,
} from './loaderPreProcessJson_Api.js';
import type { AppSiteMetadata, TemplateInfo } from '../../model/appSiteMetadata.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * ILoader interface methods to be attached to LoaderPreProcessJson prototype
 */
export const iloaderMethods = {
    // #region Helper Methods

    getBaseComponentName(this: LoaderPreProcessJson, placeholderOrSlotholderName: string, currentTemplateName: string): { baseComponentName: string; isReuseComponent: boolean } {
        const { baseTemplateName, isReusePattern } = parseComponentReusePattern(
            placeholderOrSlotholderName, currentTemplateName);
        return { baseComponentName: baseTemplateName, isReuseComponent: isReusePattern };
    },

    serializeDynamicConfigToJson(this: LoaderPreProcessJson, config: DynamicConfigMap): string {
        return serializeDynamicConfigToJsonCore(config);
    },

    deserializeStringList(this: LoaderPreProcessJson, json: JsonObject, key: string): string[] {
        const result: string[] = [];
        const val = json.tryGetValue(key);
        if (val.found && val.value.isArray) {
            const arr = val.value.asArray();
            if (arr) {
                for (const item of arr) {
                    if (item.isString) result.push(item.asString() ?? '');
                }
            }
        }
        return result;
    },

    deserializeSlotInfoList(this: LoaderPreProcessJson, values: unknown): unknown[] {
        return Array.isArray(values) ? values.map((value) => ({ ...(value as PlainJsonObject) })) : [];
    },

    deserializeTemplateInfo(this: LoaderPreProcessJson, raw: JsonObject): TemplateInfo {
        return raw as unknown as TemplateInfo;
    },

    deserializeAppSiteMetadata(this: LoaderPreProcessJson, raw: JsonObject): AppSiteMetadata {
        return raw as unknown as AppSiteMetadata;
    },

    buildMetadataJsonForAppSite(this: LoaderPreProcessJson, appSite: string): string {
        return buildMetadataJsonForAppSite(this._metadataCache, appSite);
    },

    // #endregion

    // #region Configuration

    // #endregion

    // #region DynamicData

    getDynamicDataConfig(this: LoaderPreProcessJson, appSite: string): DynamicConfigMap | null {
        return this._dynamicDataConfigStore[appSite] || null;
    },

    getDynamicDataParams(this: LoaderPreProcessJson, appSite: string, paramFile: string): JsonObject | null {
        const storeKey = `${appSite}:${paramFile}`;
        const result = this._dynamicDataParamsStore.tryGetValue(storeKey);
        return result.found && result.value.isObject ? result.value.getObject() : null;
    },

    // #region DynamicClientData

    getDynamicClientDataConfig(this: LoaderPreProcessJson, appSite: string): DynamicConfigMap | null {
        return this._dynamicClientDataConfigStore[appSite] || null;
    },

    getDynamicClientDataParams(this: LoaderPreProcessJson, appSite: string, paramFile: string): JsonObject | null {
        const storeKey = `${appSite}:${paramFile}`;
        const result = this._dynamicClientDataParamsStore.tryGetValue(storeKey);
        return result.found && result.value.isObject ? result.value.getObject() : null;
    },

    // #endregion

    getDynamicViewConfig(this: LoaderPreProcessJson, appSite: string): DynamicConfigMap | null {
        return this._dynamicViewConfigStore[appSite] || null;
    },

    getDynamicViewParams(this: LoaderPreProcessJson, appSite: string, paramFile: string): JsonObject | null {
        const storeKey = `${appSite}:${paramFile}`;
        const result = this._dynamicViewParamsStore.tryGetValue(storeKey);
        return result.found && result.value.isObject ? result.value.getObject() : null;
    },

    getDynamicActionParams(this: LoaderPreProcessJson, appSite: string, paramFile: string): JsonObject | null {
        const storeKey = `${appSite}:${paramFile}`;
        const result = this._dynamicActionParamsStore.tryGetValue(storeKey);
        return result.found && result.value.isObject ? result.value.getObject() : null;
    },

    getDynamicAppSiteConfig(this: LoaderPreProcessJson, appSite: string): DynamicConfigMap | null {
        return this._dynamicAppSiteConfigStore[appSite] || null;
    },

    getDynamicAppSiteParams(this: LoaderPreProcessJson, appSite: string, paramFile: string): JsonObject | null {
        const storeKey = `${appSite}:${paramFile}`;
        const result = this._dynamicAppSiteParamsStore.tryGetValue(storeKey);
        return result.found && result.value.isObject ? result.value.getObject() : null;
    },

    getGlobalData(this: LoaderPreProcessJson, appSite: string): JsonObject | null {
        return this._globalDataStore[appSite] || null;
    },

    getGlobalViewData(this: LoaderPreProcessJson, appSite: string): JsonObject | null {
        return this._globalViewDataStore[appSite] || null;
    },

    getGlobalViewDataForComponent(this: LoaderPreProcessJson, appSite: string, componentName: string): JsonObject | null {
        const storeKey = `${appSite}:${componentName.toLowerCase()}`;
        return this._globalViewDataComponentStore[storeKey] || null;
    },

    getDynamicActionConfig(this: LoaderPreProcessJson, appSite: string): DynamicConfigMap | null {
        return this._dynamicActionConfigStore[appSite] || null;
    },

    getDynamicActionTiming(this: LoaderPreProcessJson): { nanos: number; count: number } {
        const result = { nanos: this._dynamicActionNanos, count: this._dynamicActionCount };
        this._dynamicActionNanos = 0;
        this._dynamicActionCount = 0;
        return result;
    },

    setActionMode(this: LoaderPreProcessJson, actionMode: boolean): void {
        this._actionMode = actionMode;
    },

    getActionMode(this: LoaderPreProcessJson): boolean {
        return !!this._actionMode;
    },

    // #endregion

    // #region Dynamic Data/View Results

    setDynamicResults(this: LoaderPreProcessJson, dynamicResults: DynamicResultsJson): void {
        this._dynamicResultsJson = dynamicResults;
    },

    getDynamicDataTiming(this: LoaderPreProcessJson): { nanos: number; count: number } {
        const result = { nanos: this._dynamicDataNanos, count: this._dynamicDataCount };
        this._dynamicDataNanos = 0;
        this._dynamicDataCount = 0;
        return result;
    },

    getDynamicViewTiming(this: LoaderPreProcessJson): { nanos: number; count: number } {
        const result = { nanos: this._dynamicViewNanos, count: this._dynamicViewCount };
        this._dynamicViewNanos = 0;
        this._dynamicViewCount = 0;
        return result;
    },

    getDynamicClientDataTiming(this: LoaderPreProcessJson): { nanos: number; count: number } {
        const result = { nanos: this._dynamicClientDataNanos, count: this._dynamicClientDataCount };
        this._dynamicClientDataNanos = 0;
        this._dynamicClientDataCount = 0;
        return result;
    },

    // #endregion

    // #region Inheritance

    getAndClearInheritanceOperations(this: LoaderPreProcessJson): InheritanceOperation[] {
        const ops = this._inheritanceOperations;
        this._inheritanceOperations = [];
        return ops;
    },

    getAndClearNavigationTrace(this: LoaderPreProcessJson): NavigationDecision[] {
        const trace = this._lastNavigationTrace;
        this._lastNavigationTrace = [];
        return trace;
    },

    // #endregion

    // #region AppFile Resolution

    getAppFile(this: LoaderPreProcessJson, appSite: string, appView: string): { appFile: string; validatedAppView: string } {
        return this._config!.getAppFile(appSite, appView);
    },

    get defaultReplaceTemplateName(): string {
        return (this as unknown as LoaderPreProcessJson)._config!.defaultReplaceTemplateName;
    },

    get defaultSkipTemplateName(): string {
        return (this as unknown as LoaderPreProcessJson)._config!.defaultSkipTemplateName;
    },

    get defaultReplaceHtml(): string {
        return (this as unknown as LoaderPreProcessJson)._config!.defaultReplaceHtml;
    },

    get defaultSkipHtml(): string {
        return (this as unknown as LoaderPreProcessJson)._config!.defaultSkipHtml;
    },

    get defaultSkipJson(): string {
        return (this as unknown as LoaderPreProcessJson)._config!.defaultSkipJson;
    },

    get defaultAppViewPrefix(): string {
        return (this as unknown as LoaderPreProcessJson)._config!.defaultAppViewPrefix;
    },

    get maxArrayOperationItems(): number {
        return (this as unknown as LoaderPreProcessJson)._config!.maxArrayOperationItems;
    },

    get maxRecursionDepth(): number {
        return (this as unknown as LoaderPreProcessJson)._config!.maxRecursionDepth;
    },

    get maxMergeIterations(): number {
        return (this as unknown as LoaderPreProcessJson)._config!.maxMergeIterations;
    },

    // #endregion

    // #region API Support

    getTemplatesApiResponse(
        this: LoaderPreProcessJson,
        appSite: string,
        appView: string,
        contextJson: JsonObject,
        serverTimeMs: number,
        includeSearchAppSites: boolean = true,
        maskHtml: boolean = false,
        orderedTemplateNames: string[] | null = null
    ): string {
        const appFileResult = this._config!.getAppFile(appSite, appView);
        const appFile = appFileResult.appFile;

        const templatesDict = getAllTemplatesJson(this, this._htmlJsonCache, this._dataJsonCache, this._metadataCache, this._appSite, this._config!, includeSearchAppSites, maskHtml, orderedTemplateNames);
        const config = this._config!;

        const sb: string[] = [];
        sb.push('{"PreProcessTemplates":{');

        let first = true;
        for (const key in templatesDict) {
            if (!first) sb.push(',');
            first = false;

            sb.push('"');
            sb.push(JsonUtility.escapeJson(key));
            sb.push('":');
            sb.push(templatesDict[key].toJson());
        }

        sb.push('}');
        sb.push(',"AppSite":"');
        sb.push(JsonUtility.escapeJson(appSite));
        sb.push('","AppFile":"');
        sb.push(JsonUtility.escapeJson(appFile));
        sb.push('","AppView":"');
        sb.push(JsonUtility.escapeJson(appView));
        sb.push('","SearchAppSites":"');
        sb.push(JsonUtility.escapeJson(this.getSearchAppSitesForAppSite(appSite)));
        sb.push('","Config":{');
        sb.push('"defaultReplaceHtml":"');
        sb.push(JsonUtility.escapeJson(config.defaultReplaceHtml));
        sb.push('","defaultSkipHtml":"');
        sb.push(JsonUtility.escapeJson(config.defaultSkipHtml));
        sb.push('","defaultSkipJson":"');
        sb.push(JsonUtility.escapeJson(config.defaultSkipJson));
        sb.push('","defaultReplaceTemplateName":"');
        sb.push(JsonUtility.escapeJson(config.defaultReplaceTemplateName));
        sb.push('","defaultSkipTemplateName":"');
        sb.push(JsonUtility.escapeJson(config.defaultSkipTemplateName));
        sb.push('","defaultAppViewPrefix":"');
        sb.push(JsonUtility.escapeJson(config.defaultAppViewPrefix));
        sb.push('","maxRecursionDepth":');
        sb.push(config.maxRecursionDepth.toString());
        sb.push(',"maxArrayOperationItems":');
        sb.push(config.maxArrayOperationItems.toString());
        sb.push(',"dynamicDataUseResolvedAppSite":');
        sb.push(config.dynamicDataUseResolvedAppSite ? 'true' : 'false');
        sb.push('}');
        sb.push(',"ContextJson":');
        sb.push(JsonUtility.serializeObject(contextJson));

        this._serializePerSiteStores(sb, appSite);

        sb.push(',"ServerTimeMs":');
        sb.push(serverTimeMs.toString());
        sb.push('}');

        return sb.join('');
    },

    setTransientStore(this: LoaderPreProcessJson, transientStore: ITransientStore): void {
        this._transientStore = transientStore;
    },

    getTransientStore(this: LoaderPreProcessJson): ITransientStore {
        return this._transientStore;
    },

    _serializePerSiteStores(this: LoaderPreProcessJson, sb: string[], appSite: string): void {
        const allSites = [appSite];
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !allSites.includes(trimmed)) allSites.push(trimmed);
            }
        }
        // Also cover any other loaded AppSite (e.g. a dynamic-AppSite redirect target that Templates
        // carries) so its dynamic stores ship too; otherwise the client re-resolves to that AppSite but
        // lacks its DynamicData/View config and renders a partial result. Sorted for byte-stable output.
        const extraSites: string[] = [];
        for (const site of this._htmlJsonCache.keys()) {
            if (!allSites.includes(site) && !extraSites.includes(site)) extraSites.push(site);
        }
        for (const site of this._dataJsonCache.keys()) {
            if (!allSites.includes(site) && !extraSites.includes(site)) extraSites.push(site);
        }
        extraSites.sort((a, b) => { const la = a.toLowerCase(), lb = b.toLowerCase(); return la < lb ? -1 : la > lb ? 1 : 0; });
        for (const site of extraSites) allSites.push(site);
        sb.push(',"PerSiteStores":{');

        sb.push('"GlobalData":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(JsonUtility.escapeJson(allSites[i])); sb.push('":');
            const gd = this.getGlobalData(allSites[i]);
            sb.push(gd ? JsonUtility.serializeObject(gd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewData":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(JsonUtility.escapeJson(allSites[i])); sb.push('":');
            const gvd = this.getGlobalViewData(allSites[i]);
            if (gvd) {
                const resolved = new JsonObject();
                const viewDataKeys: string[] = Array.from(gvd.keys());
                for (const componentName of viewDataKeys) {
                    const resolvedData = this.getGlobalViewDataForComponent(allSites[i], componentName);
                    if (resolvedData) resolved.set(componentName, resolvedData);
                }
                sb.push(resolved.size > 0 ? JsonUtility.serializeObject(resolved) : 'null');
            } else {
                sb.push('null');
            }
        }
        sb.push('},');

        // The client re-merge ships the config of every handler tier that EXECUTES on the client: DynamicView,
        // and the PURE client bindings below. Server-only Data/Action/AppSite handlers — and the Embedded
        // client-data PREPARER — stay unshipped; their results are already baked into the shipped templates
        // or arrive as the delivered island.
        this._serializePerSiteDynamicConfig(sb, allSites, 'DynamicViewConfig', (site: string) => this.getDynamicViewConfig(site));
        sb.push(',');
        this._serializePerSiteParamsFromStore(sb, allSites, 'DynamicViewParams', this._dynamicViewParamsStore);
        sb.push(',');
        // PURE client bindings + the params they name. A pure handler resolves its params from the BINDING
        // (tryResolvePureClientHandlerParams), so without these the reconstruction sources silently skip it
        // and the replay diverges from the server render.
        this._serializePerSiteDynamicConfig(sb, allSites, 'DynamicClientConfig', (site: string) => CommonUtil.filterPureClientBindings(this.getDynamicClientDataConfig(site)));
        sb.push(',');
        this._serializePerSiteParamsFromStore(sb, allSites, 'DynamicClientParams',
            CommonUtil.filterPureClientParams(this._dynamicClientDataParamsStore, allSites, (site: string) => this.getDynamicClientDataConfig(site)));

        sb.push('}');
    },

    _serializePerSiteDynamicConfig(this: LoaderPreProcessJson, sb: string[], allSites: string[], key: string, getter: (site: string) => DynamicConfigMap | null): void {
        sb.push('"'); sb.push(key); sb.push('":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(JsonUtility.escapeJson(allSites[i])); sb.push('":');
            const config = getter(allSites[i]);
            sb.push(config ? this.serializeDynamicConfigToJson(config) : 'null');
        }
        sb.push('}');
    },

    _serializePerSiteParamsFromStore(this: LoaderPreProcessJson, sb: string[], allSites: string[], key: string, paramsStore: JsonObject): void {
        sb.push('"'); sb.push(key); sb.push('":{');
        let firstSite = true;
        for (let i = 0; i < allSites.length; i++) {
            const site = allSites[i];
            const prefix = `${site}:`;
            const siteParams = new JsonObject();
            for (const storeKey of paramsStore.keys()) {
                if (storeKey.toLowerCase().startsWith(prefix.toLowerCase())) {
                    const storeValue = paramsStore.get(storeKey);
                    if (storeValue.isObject) {
                        siteParams.set(storeKey.substring(prefix.length), storeValue.getObject());
                    }
                }
            }
            if (siteParams.size === 0) continue;
            if (!firstSite) sb.push(',');
            firstSite = false;
            sb.push('"'); sb.push(JsonUtility.escapeJson(site)); sb.push('":{');
            let firstParam = true;
            for (const paramKey of siteParams.keys()) {
                if (!firstParam) sb.push(',');
                firstParam = false;
                sb.push('"'); sb.push(JsonUtility.escapeJson(paramKey)); sb.push('":');
                sb.push(JsonUtility.serializeObject(siteParams.get(paramKey)));
            }
            sb.push('}');
        }
        sb.push('}');
    },

    getComponentId(this: LoaderPreProcessJson, appSite: string, componentName: string): string | null {
        let dynamicConfig = this.getDynamicViewConfig(appSite);
        let configAppSite: string | null = dynamicConfig ? appSite : null;

        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicViewConfig(trimmed);
                    if (dynamicConfig) {
                        configAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (!dynamicConfig) return null;

        let matchedKey: string | null = null;
        const componentNameLower = componentName.toLowerCase();
        for (const key of dynamicConfig.keys()) {
            if (key !== '*' && key.toLowerCase() === componentNameLower) {
                matchedKey = key;
                break;
            }
        }
        if (matchedKey === null && dynamicConfig.has('*')) {
            matchedKey = '*';
        }

        if (matchedKey === null) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (!searchConfig) continue;

                    for (const key of searchConfig.keys()) {
                        if (key !== '*' && key.toLowerCase() === componentNameLower) {
                            matchedKey = key;
                            dynamicConfig = searchConfig;
                            configAppSite = trimmed;
                            break;
                        }
                    }
                    if (matchedKey !== null) break;

                    if (searchConfig.has('*')) {
                        matchedKey = '*';
                        dynamicConfig = searchConfig;
                        configAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (matchedKey === null) {
            const configsToSearch: Array<{ config: DynamicConfigMap; appSite: string }> = [];
            configsToSearch.push({ config: dynamicConfig, appSite: configAppSite || '' });

            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (searchConfig && !configsToSearch.some(entry => entry.appSite === trimmed)) {
                        configsToSearch.push({ config: searchConfig, appSite: trimmed });
                    }
                }
            }

            for (const entry of configsToSearch) {
                for (const [, handlers] of entry.config) {
                    for (const handler of handlers) {
                        if (handler.iType.toLowerCase() !== 'baseview' || !handler.iParamFile) continue;
                        const paramJson = this.getDynamicViewParams(entry.appSite, handler.iParamFile);
                        if (!paramJson || !paramJson.has('ComponentID')) continue;
                        const componentIdValue = paramJson.get('ComponentID');
                        const componentId = componentIdValue.isString
                            ? componentIdValue.asString()
                            : componentIdValue.toString();
                        if (componentId?.toLowerCase() === componentNameLower) return componentId;
                    }
                }
            }
            return null;
        }

        const handlersValue = dynamicConfig.get(matchedKey);
        if (!Array.isArray(handlersValue)) return null;

        let baseViewParamFile: string | null = null;
        for (const handlerObj of handlersValue as unknown as Array<PlainJsonObject>) {
            if (!handlerObj || typeof handlerObj !== 'object') continue;
            const iViewType = handlerObj.iType as string | undefined;
            if (!iViewType) continue;

            if (iViewType.toLowerCase() === 'baseview') {
                if (handlerObj.iParamFile) {
                    baseViewParamFile = handlerObj.iParamFile as string;
                }
                break;
            }
        }

        if (baseViewParamFile === null) return null;

        const paramJson = this.getDynamicViewParams(configAppSite!, baseViewParamFile);
        if (paramJson) {
            const componentIdValue = paramJson.get('ComponentID');
            if (componentIdValue && componentIdValue.isString) {
                const componentId = componentIdValue.asString()!;
                if (componentId) return componentId;
            }
        }

        if (!componentName) return componentName;
        return componentName.charAt(0).toLowerCase() + componentName.substring(1);
    }

    // #endregion
};

/**
 * Static methods for ILoader interface
 */
export const iloaderStaticMethods = {};
